import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { StartupWhatsNewItem, StartupWhatsNewWindow } from '@/types/startup';
import type { RecordsSectionId } from './RecordsPanel';
import type { SectionHint } from './section-hints';
import { formatRecordDate } from './records-utils';

interface SectionListProps {
  patientFirstName: string;
  whatsNew?: StartupWhatsNewItem[];
  whatsNewWindow?: StartupWhatsNewWindow;
  hints?: Partial<Record<RecordsSectionId, SectionHint>>;
  sections: Array<{ id: RecordsSectionId; label: string }>;
  onOpen: (section: RecordsSectionId) => void;
  onAskQuestion: (question: string, displayText?: string) => void;
  variant: 'main' | 'rail';
}

export default function SectionList({ patientFirstName, whatsNew, whatsNewWindow, hints, sections, onOpen, onAskQuestion, variant }: SectionListProps) {
  const { t, i18n } = useTranslation();
  const windowLabel = whatsNewWindow?.kind === 'sinceVisit'
    ? t('records.whatsNewSinceVisit', { date: formatRecordDate(whatsNewWindow.since, i18n.resolvedLanguage || 'en') })
    : t('records.whatsNewLast24h');

  return (
    <div className={variant === 'rail' ? 'p-4' : 'max-w-3xl mx-auto px-4 sm:px-6 py-5 md:pr-14'}>
      {whatsNew && whatsNew.length > 0 && (
        <section className="rounded-lg border bg-gray-50 p-4 mb-4" data-testid="whats-new-card">
          <h3 className="text-sm font-semibold text-text-primary">{t('records.whatsNew')}</h3>
          <p className="text-xs text-text-secondary mb-1">{windowLabel}</p>
          <div className="-mx-1.5">
            {whatsNew.map((item, index) => (
              <button
                key={index}
                className="w-full flex items-center gap-2 py-2 px-1.5 text-left rounded-md hover:bg-gray-100 transition-colors"
                onClick={() => onAskQuestion(item.ask, item.text)}
                data-testid={`button-whats-new-${index}`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                <span className="text-sm text-text-primary flex-1">{item.text}</span>
                <span className="text-sm text-primary flex-shrink-0 font-medium">{t('records.ask')}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      <p className="text-sm text-text-secondary mb-4">{t('records.everythingStillHere', { name: patientFirstName })}</p>
      <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">{t('dashboard.allMedicalInformation')}</h3>
      <div className="rounded-lg border divide-y overflow-hidden bg-white">
        {sections.map((section) => (
          <button
            key={section.id}
            onClick={() => onOpen(section.id)}
            className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
            aria-label={t('records.openSection', { section: section.label })}
            data-testid={`section-${section.id}`}
          >
            <span className="flex items-center gap-2 min-w-0">
              {hints?.[section.id]?.fresh && <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" data-testid={`fresh-dot-${section.id}`} />}
              <span className="text-base font-medium text-text-primary min-w-0">{section.label}</span>
            </span>
            <span className="flex items-center gap-2 flex-shrink-0">
              {hints?.[section.id] && (
                <span className="text-xs text-text-secondary">{t(hints[section.id]!.key, hints[section.id]!.params)}</span>
              )}
              <ChevronRight className="h-4 w-4 text-text-secondary flex-shrink-0" />
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
